import type { ExposureType, Ticker } from '../store/heatmapStore';

export interface TrinityPanelState {
  ticker: Ticker;
  type: ExposureType;
}

export const TRINITY_PANEL_COUNT = 3;

export const DEFAULT_TRINITY_PANELS: readonly TrinityPanelState[] = [
  { ticker: 'SPY', type: 'gex' },
  { ticker: 'QQQ', type: 'gex' },
  { ticker: 'IWM', type: 'vex' },
];

const STORAGE_KEY = 'mvai-trinity-panels';

function isExposureType(value: unknown): value is ExposureType {
  return value === 'gex' || value === 'vex';
}

function isTrinityPanelState(value: unknown): value is TrinityPanelState {
  if (typeof value !== 'object' || value == null) {
    return false;
  }

  const panel = value as Partial<TrinityPanelState>;
  return typeof panel.ticker === 'string' && panel.ticker !== '' && isExposureType(panel.type);
}

export function loadStoredTrinityPanels(): TrinityPanelState[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw == null) {
      return [...DEFAULT_TRINITY_PANELS];
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length !== TRINITY_PANEL_COUNT) {
      return [...DEFAULT_TRINITY_PANELS];
    }

    if (!parsed.every(isTrinityPanelState)) {
      return [...DEFAULT_TRINITY_PANELS];
    }

    return parsed.map((panel) => ({ ticker: panel.ticker.toUpperCase(), type: panel.type }));
  } catch {
    return [...DEFAULT_TRINITY_PANELS];
  }
}

export function saveStoredTrinityPanels(panels: readonly TrinityPanelState[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(panels));
  } catch {
    /* private browsing or storage quota — panel state stays session-local */
  }
}

/**
 * Replace panel tickers that are no longer in the enabled universe. Each
 * replaced panel prefers an enabled ticker not already used by another panel
 * so the default view stays a three-way comparison.
 */
export function reconcileTrinityPanels(
  panels: readonly TrinityPanelState[],
  enabledTickers: readonly Ticker[],
): TrinityPanelState[] {
  if (enabledTickers.length === 0) {
    return [...panels];
  }

  const enabledSet = new Set(enabledTickers);
  const usedTickers = new Set(panels.map((panel) => panel.ticker));

  return panels.map((panel) => {
    if (enabledSet.has(panel.ticker)) {
      return panel;
    }

    const replacement =
      enabledTickers.find((ticker) => !usedTickers.has(ticker)) ?? enabledTickers[0];
    usedTickers.add(replacement);
    return { ...panel, ticker: replacement };
  });
}
